import React, { useState, useEffect, useMemo } from "react";
import { Play } from "lucide-react";
import { fetchBatchURLMetadata, isPotentialYouTubeURL } from "../../utils/urlMetadata";
import logger from "../../services/Logger";

// Utility function to format view count
const formatViewCount = (count) => {
  if (!count) return '';
  const num = parseInt(count);
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M views';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K views';
  }
  return num + ' views';
};

// YouTube Video Card Component - now receives videoInfo directly from batch processing
const YouTubeVideoCard = ({ videoInfo, index }) => {
  if (!videoInfo) {
    return null;
  }

  // Use fallback thumbnail if none provided
  const thumbnailUrl = videoInfo.thumbnail || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiBmaWxsPSIjMTgxODE4Ii8+CjxjaXJjbGUgY3g9IjE2MCIgY3k9IjkwIiByPSIzMCIgZmlsbD0iI0ZGRkZGRiIgZmlsbC1vcGFjaXR5PSIwLjgiLz4KPHBhdGggZD0iTTE0NSA3NUwxNzUgOTBMMTQ1IDEwNVY3NVoiIGZpbGw9IiMxODE4MTgiLz4KPC9zdmc+';

  return (
    <a
      href={videoInfo.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative bg-fill-black rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all duration-200 aspect-video"
    >
      {/* Video Thumbnail */}
      <div className="relative w-full h-full">
        <img
          src={thumbnailUrl}
          alt={videoInfo.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
          onError={(e) => {
            // Fallback to a clean video placeholder
            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiBmaWxsPSIjMTgxODE4Ii8+CjxjaXJjbGUgY3g9IjE2MCIgY3k9IjkwIiByPSIzMCIgZmlsbD0iI0ZGRkZGRiIgZmlsbC1vcGFjaXR5PSIwLjgiLz4KPHBhdGggZD0iTTE0NSA3NUwxNzUgOTBMMTQ1IDEwNVY3NVoiIGZpbGw9IiMxODE4MTgiLz4KPC9zdmc+';
          }}
        />

        {/* Duration badge */}
        {videoInfo.duration && (
          <div className="absolute bottom-2 right-2 bg-fill-black/90 text-ink-on-color text-xs px-2 py-1 rounded font-medium backdrop-blur-sm">
            {videoInfo.duration}
          </div>
        )}
      </div>

      {/* Play Button Overlay */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="bg-fill-red-600 hover:bg-fill-red-700 rounded-full p-3 group-hover:scale-110 transition-transform duration-200 shadow-lg">
          <Play className="w-4 h-4 text-ink-white ml-0.5" />
        </div>
      </div>

      {/* Video Title Overlay */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-gradient-black/95 via-gradient-black/70 to-transparent p-3 backdrop-blur-sm">
        <h4 className="text-ink-white text-xs font-semibold line-clamp-2 leading-tight drop-shadow-lg" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}>
          {videoInfo.title}
        </h4>
        <div className="flex items-center justify-between mt-1">
          {videoInfo.author && (
            <p className="text-ink-gray-200 text-xs line-clamp-1 flex-1 drop-shadow-md" style={{ textShadow: '1px 1px 1px rgba(0,0,0,0.6)' }}>
              {videoInfo.author}
            </p>
          )}
          {videoInfo.view_count && (
            <span className="text-ink-gray-200 text-xs ml-2 drop-shadow-md" style={{ textShadow: '1px 1px 1px rgba(0,0,0,0.6)' }}>
              {formatViewCount(videoInfo.view_count)}
            </span>
          )}
        </div>
      </div>

      {/* Hover overlay */}
      <div className="absolute inset-0 bg-fill-black/0 group-hover:bg-fill-black/10 transition-colors duration-200"></div>
    </a>
  );
};

const YouTubeVideosSection = ({ sources }) => {
  const [videosInfo, setVideosInfo] = useState([]);
  const [loading, setLoading] = useState(true);

  // Memoize topVideos to prevent infinite re-renders
  // (filter/slice creates new array reference each render, causing useEffect loop)
  const topVideos = useMemo(() => {
    const validSources =
      sources?.filter((source) => {
        const url =
          source?.web?.url || source?.web?.uri || source?.url || source?.uri;
        return url && (url.startsWith("http://") || url.startsWith("https://"));
      }) || [];

    const youtubeVideos = validSources.filter((source) => {
      const url = source?.web?.url || source?.web?.uri || source?.url || source?.uri;
      const title = source?.web?.title || source?.title;
      return isPotentialYouTubeURL(url, title);
    });

    return youtubeVideos.slice(0, 4);
  }, [sources]);

  useEffect(() => {
    let cancelled = false;

    const fetchAllVideoMetadata = async () => {
      if (topVideos.length === 0) {
        if (!cancelled) setLoading(false);
        return;
      }

      try {
        if (!cancelled) setLoading(true);

        // Extract URLs and prepare for batch processing
        const videoUrls = topVideos.map(source =>
          source?.web?.url || source?.web?.uri || source?.url || source?.uri
        );

        // Check if any URLs need redirect info (Vertex AI)
        const includeRedirectInfo = videoUrls.some(url =>
          url.includes('vertexaisearch.cloud.google.com')
        );

        // Fetch all video metadata in a single batch call
        const metadataArray = await fetchBatchURLMetadata(videoUrls, includeRedirectInfo);

        // Process the batch results
        const processedVideos = metadataArray.map((metadata, index) => {
          const source = topVideos[index];
          const sourceUrl = videoUrls[index];
          const sourceTitle = source?.web?.title || source?.title;

          let finalThumbnail = metadata.thumbnail;
          let extractedVideoId = metadata.video_id;
          let finalTitle = metadata.title;

          // Prefer backend thumbnail, but fallback to YouTube direct URL if needed
          if (extractedVideoId && !finalThumbnail) {
            finalThumbnail = `https://img.youtube.com/vi/${extractedVideoId}/mqdefault.jpg`;
          }

          // If we still don't have video ID, try extracting from final URL as fallback
          if (!extractedVideoId && metadata.redirect_info?.final_url) {
            const videoIdMatch = metadata.redirect_info.final_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
            if (videoIdMatch) {
              extractedVideoId = videoIdMatch[1];
              if (!finalThumbnail) {
                finalThumbnail = `https://img.youtube.com/vi/${extractedVideoId}/mqdefault.jpg`;
              }
            }
          }

          // Use better title fallback
          if (!finalTitle || finalTitle === '- YouTube' || finalTitle === 'YouTube') {
            finalTitle = sourceTitle || 'YouTube Video';
          }

          return {
            id: extractedVideoId || `video-${index}`,
            title: finalTitle || sourceTitle || 'YouTube Video',
            thumbnail: finalThumbnail,
            url: metadata.redirect_info?.final_url || metadata.url || sourceUrl,
            author: metadata.author,
            description: metadata.description,
            domain: metadata.domain,
            duration: metadata.duration,
            view_count: metadata.view_count
          };
        });

        if (!cancelled) {
          setVideosInfo(processedVideos);
          setLoading(false);
        }
      } catch (err) {
        logger.warn('YouTubeVideosSection', 'Failed to fetch batch video metadata', { error: err.message });

        // Fallback: create basic video info from sources
        const fallbackVideos = topVideos.map((source, index) => {
          const sourceUrl = source?.web?.url || source?.web?.uri || source?.url || source?.uri;
          const sourceTitle = source?.web?.title || source?.title;

          return {
            id: `fallback-${index}`,
            title: sourceTitle || 'Video',
            thumbnail: null,
            url: sourceUrl
          };
        });

        if (!cancelled) {
          setVideosInfo(fallbackVideos);
          setLoading(false);
        }
      }
    };

    fetchAllVideoMetadata();

    return () => {
      cancelled = true;
    };
  }, [topVideos]); // Re-run when videos change

  // Only render if there are YouTube videos
  if (topVideos.length === 0) {
    return null;
  }

  if (loading) {
    return (
      <div className="mt-6 mb-4">
        <div
          data-videos-section
          className="pt-6 border-t-2 border-edge-red-100 dark:border-edge-red-900/25 bg-gradient-to-r from-gradient-red-50/50 to-gradient-hex-fdf2f8/50 dark:from-gradient-hex-450a0a/10 dark:to-gradient-hex-500724/10 rounded-xl p-5"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {Array.from({ length: topVideos.length }).map((_, index) => (
              <div key={index} className="group relative bg-fill-gray-200 dark:bg-fill-gray-700 rounded-lg overflow-hidden shadow-md aspect-video animate-pulse">
                <div className="w-full h-full bg-fill-gray-300 dark:bg-fill-gray-600"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 mb-4">
      <div
        data-videos-section
        className="pt-6 border-t-2 border-edge-red-100 dark:border-edge-red-900/25 bg-gradient-to-r from-gradient-red-50/50 to-gradient-hex-fdf2f8/50 dark:from-gradient-hex-450a0a/10 dark:to-gradient-hex-500724/10 rounded-xl p-5"
      >
        {/* Show videos from batch processing */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {videosInfo.map((video) => (
            <YouTubeVideoCard key={video.id} videoInfo={video} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default YouTubeVideosSection;
